import { Copy } from "lucide-react";
import { extractEmailAddress } from "@/lib/compose/composeDraft";
import { extractSenderLabel } from "@/lib/formatMailDate";
import { showMailToast } from "@/lib/ui/mailToast";
import { cn } from "@/lib/cn";

type Props = {
  from: string;
  dateLabel: string;
  className?: string;
};

function isLikelyEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value.trim());
}

function CopyEmailButton({ email, className }: { email: string; className?: string }) {
  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(email);
      showMailToast("Adresse copiée", "success", 2200);
    } catch {
      showMailToast("Copie impossible", "error", 2800);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copyEmail()}
      aria-label="Copier l'adresse"
      className={cn(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]",
        className
      )}
    >
      <Copy className="h-3 w-3" aria-hidden />
    </button>
  );
}

/** Nom + email copiable dans le lecteur (sans tooltip native sur le bloc). */
export function MessageSenderMeta({ from, dateLabel, className }: Props) {
  const sender = extractSenderLabel(from) || "Expéditeur inconnu";
  const email = extractEmailAddress(from).trim().toLowerCase();
  const hasEmail = isLikelyEmail(email);
  const showEmailLine = hasEmail && sender.trim().toLowerCase() !== email;

  return (
    <div className={cn("min-w-0 flex-1", className)}>
      <div className="flex min-w-0 items-baseline gap-1.5">
        <p className="min-w-0 truncate text-xs font-semibold text-[var(--mail-text)]">{sender}</p>
        {hasEmail && !showEmailLine ? <CopyEmailButton email={email} /> : null}
        <span className="ml-auto shrink-0 text-[10px] tabular-nums text-[var(--mail-muted)]">
          {dateLabel}
        </span>
      </div>
      {showEmailLine ? (
        <div className="mt-0.5 flex min-w-0 items-center gap-0.5">
          <span className="min-w-0 truncate text-[11px] text-[var(--mail-muted)]">{email}</span>
          <CopyEmailButton email={email} />
        </div>
      ) : null}
    </div>
  );
}
